import React from 'react';
import { X, AlertTriangle, Upload, Plus } from 'lucide-react';
interface CreateClaimModalProps {
  isOpen: boolean;
  onClose: () => void;
  productSku: string;
  productName: string;
}
export function CreateClaimModal({
  isOpen,
  onClose,
  productSku,
  productName
}: CreateClaimModalProps) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50" data-id="element-185">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" data-id="element-186">
        <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-187">
          <div data-id="element-188">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-189">
              Create New Product Claim
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-190">
              Add a new claim for regulatory review
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-191">
            <X className="w-5 h-5" data-id="element-192" />
          </button>
        </div>
        <div className="p-6" data-id="element-193">
          <form className="space-y-6" data-id="element-194">
            {/* Product Info */}
            <div className="bg-gray-50 p-4 rounded-lg" data-id="element-195">
              <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-196">
                <div data-id="element-197">
                  <span className="text-gray-600" data-id="element-198">Product SKU:</span>
                  <span className="ml-2 font-medium text-gray-900" data-id="element-199">
                    {productSku}
                  </span>
                </div>
                <div data-id="element-200">
                  <span className="text-gray-600" data-id="element-201">Product Name:</span>
                  <span className="ml-2 font-medium text-gray-900" data-id="element-202">
                    {productName}
                  </span>
                </div>
              </div>
            </div>
            {/* Claim Text */}
            <div data-id="element-203">
              <label htmlFor="claim" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-204">
                Claim Text*
              </label>
              <textarea id="claim" rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="Enter the exact claim text as it will appear on packaging..." data-id="element-205" />
            </div>
            {/* Claim Type */}
            <div data-id="element-206">
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-207">
                Claim Type*
              </label>
              <select id="type" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-208">
                <option value="" data-id="element-209">Select a claim type...</option>
                <option value="structure-function" data-id="element-210">
                  Structure/Function Claim
                </option>
                <option value="content" data-id="element-211">Content Claim</option>
                <option value="health" data-id="element-212">Health Claim</option>
                <option value="nutrient" data-id="element-213">Nutrient Content Claim</option>
              </select>
            </div>
            {/* Supporting Evidence */}
            <div data-id="element-214">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-215">
                Supporting Documentation
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6" data-id="element-216">
                <div className="flex flex-col items-center" data-id="element-217">
                  <Upload className="w-8 h-8 text-gray-400 mb-2" data-id="element-218" />
                  <p className="text-sm text-gray-600 text-center mb-2" data-id="element-219">
                    Drag and drop files here, or click to select files
                  </p>
                  <button type="button" className="text-purple-600 text-sm font-medium" data-id="element-220">
                    Browse Files
                  </button>
                </div>
              </div>
            </div>
            {/* Reference IDs */}
            <div data-id="element-221">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-222">
                Study/Reference IDs
              </label>
              <div className="space-y-2" data-id="element-223">
                <div className="flex items-center space-x-2" data-id="element-224">
                  <input type="text" className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="Enter reference ID..." data-id="element-225" />
                  <button type="button" className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg" data-id="element-226">
                    <Plus className="w-5 h-5" data-id="element-227" />
                  </button>
                </div>
              </div>
            </div>
            {/* FDA Warning */}
            <div className="flex items-start space-x-3 p-4 bg-yellow-50 rounded-lg" data-id="element-228">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-229" />
              <div className="text-sm text-yellow-600" data-id="element-230">
                <p className="font-medium mb-1" data-id="element-231">FDA Compliance Notice</p>
                <p data-id="element-232">
                  All claims must comply with FDA regulations.
                  Structure/function claims require FDA notification within 30
                  days of first marketing.
                </p>
              </div>
            </div>
          </form>
        </div>
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200" data-id="element-233">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:text-gray-900" data-id="element-234">
            Cancel
          </button>
          <button type="submit" className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2" data-id="element-235">
            Create Claim
          </button>
        </div>
      </div>
    </div>;
}